import React, { useState, useEffect } from 'react';
import { X, Search, ClipboardList, UserMinus, AlertCircle, UserPlus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import API_URL from '../config';
import '../pages/Home.css';
import '../pages/Profile.css';

const ReviewersModal = ({ taskId, taskName, onClose, requesterEmail }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [reviewers, setReviewers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => { fetchReviewers(); }, [taskId]);

    const fetchReviewers = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/formation/tasks/${taskId}/reviewers`);
            const data = await res.json();
            if (data.reviewers) setReviewers(data.reviewers);
        } catch (err) {
            console.error('Failed to fetch reviewers:', err);
        } finally { setLoading(false); }
    };

    const handleSearch = async (val) => {
        setSearchQuery(val);
        if (val.length < 2) { setSearchResults([]); return; }
        setSearchLoading(true);
        try {
            const res = await fetch(`${API_URL}/formation/faculty-search?query=${encodeURIComponent(val)}`);
            const data = await res.json();
            if (data.faculty) setSearchResults(data.faculty);
        } catch (err) { console.error('Search failed:', err); }
        finally { setSearchLoading(false); }
    };

    const addReviewer = async (faculty) => {
        setError(null);
        try {
            const res = await fetch(`${API_URL}/formation/tasks/${taskId}/reviewers`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: faculty.email, requester_email: requesterEmail })
            });
            const data = await res.json();
            if (data.success) {
                setSearchResults([]);
                setSearchQuery('');
                fetchReviewers();
            } else { setError(data.error); }
        } catch (err) { setError('Failed to add reviewer'); }
    };

    const removeReviewer = async (email) => {
        if (!window.confirm('Remove review access for this faculty?')) return;
        try {
            const res = await fetch(
                `${API_URL}/formation/tasks/${taskId}/reviewers/${encodeURIComponent(email)}?requester_email=${encodeURIComponent(requesterEmail)}`,
                { method: 'DELETE' }
            );
            const data = await res.json();
            if (data.success) fetchReviewers();
        } catch (err) { console.error('Failed to remove reviewer:', err); }
    };

    return (
        <div onClick={onClose} style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
            <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="glass-card"
                style={{ width: '90%', maxWidth: '520px', padding: '2rem', position: 'relative' }}
                onClick={e => e.stopPropagation()}
            >
                <button onClick={onClose} style={{
                    position: 'absolute', top: '1rem', right: '1rem',
                    background: 'rgba(255,255,255,0.08)', border: '1px solid var(--glass-border)',
                    borderRadius: '10px', padding: '6px', cursor: 'pointer', color: 'var(--text-main)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <X size={18} />
                </button>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '0.4rem' }}>
                    <div style={{
                        width: 42, height: 42, borderRadius: '12px',
                        background: 'linear-gradient(135deg,#f59e0b,#ef4444)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                        <ClipboardList size={20} color="white" />
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700 }}>Manage Reviewers</h2>
                        <p style={{ margin: 0, opacity: 0.6, fontSize: '0.82rem' }}>Review-only access · {taskName}</p>
                    </div>
                </div>

                <div style={{
                    background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)',
                    borderRadius: '10px', padding: '10px 14px', marginBottom: '1.4rem', marginTop: '1rem',
                    fontSize: '0.82rem', color: '#f59e0b', display: 'flex', gap: '8px', alignItems: 'flex-start'
                }}>
                    <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>Reviewers can only access the <strong>Review Dashboard</strong> — they cannot modify room settings or teams.</span>
                </div>

                {/* Search */}
                <div style={{ marginBottom: '1rem', position: 'relative', display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', borderRadius: '12px', padding: '10px 14px' }}>
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search faculty by name or Emp ID..."
                        value={searchQuery}
                        onChange={e => handleSearch(e.target.value)}
                        autoFocus
                        style={{
                            flex: 1, background: 'transparent', border: 'none', outline: 'none',
                            color: 'var(--text-main)', fontSize: '0.9rem'
                        }}
                    />
                    {searchLoading && (
                        <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', opacity: 0.5, fontSize: '0.75rem' }}>
                            Searching...
                        </div>
                    )}
                </div>

                {error && (
                    <div style={{
                        background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                        borderRadius: '8px', padding: '8px 14px', marginBottom: '1rem',
                        color: '#ef4444', fontSize: '0.85rem', display: 'flex', gap: '8px', alignItems: 'center'
                    }}>
                        <AlertCircle size={14} /> {error}
                    </div>
                )}

                {/* Search Results Dropdown */}
                <AnimatePresence>
                    {searchResults.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            className="glass-card"
                            style={{ marginBottom: '1rem', overflow: 'hidden', padding: '0.4rem' }}
                        >
                            {searchResults.map(f => (
                                <button
                                    key={f.email}
                                    style={{
                                        width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        padding: '0.75rem', gap: '12px', background: 'transparent', border: 'none',
                                        cursor: 'pointer', borderRadius: '10px', transition: 'background 0.15s', textAlign: 'left',
                                        color: 'var(--text-main)'
                                    }}
                                    onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                                    onClick={() => addReviewer(f)}
                                >
                                    <div style={{ textAlign: 'left', flex: 1 }}>
                                        <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>{f.name}</div>
                                        <div style={{ fontSize: '0.78rem', opacity: 0.6 }}>{f.emp_id} · {f.email}</div>
                                    </div>
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                        background: 'rgba(245,158,11,0.12)', color: '#f59e0b',
                                        padding: '4px 10px', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 600
                                    }}>
                                        <UserPlus size={13} /> Add
                                    </div>
                                </button>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Current Reviewers List */}
                <div>
                    <h3 style={{ fontSize: '0.9rem', fontWeight: '700', marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: '8px', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <ClipboardList size={14} /> Assigned Reviewers ({reviewers.length})
                    </h3>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '1.5rem', opacity: 0.5 }}>Loading...</div>
                    ) : reviewers.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', opacity: 0.4, fontSize: '0.9rem' }}>
                            No reviewers assigned yet
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {reviewers.map(r => (
                                <div key={r.email} style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    background: 'rgba(255,255,255,0.04)', border: '1px solid var(--glass-border)',
                                    borderRadius: '12px', padding: '10px 14px'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{r.name || r.email}</div>
                                        <div style={{ fontSize: '0.78rem', opacity: 0.55 }}>{r.emp_id ? `${r.emp_id} · ` : ''}{r.email}</div>
                                    </div>
                                    <button
                                        onClick={() => removeReviewer(r.email)}
                                        title="Remove reviewer access"
                                        style={{
                                            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
                                            borderRadius: '8px', padding: '6px', cursor: 'pointer', color: '#ef4444',
                                            display: 'flex', alignItems: 'center'
                                        }}
                                    >
                                        <UserMinus size={15} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default ReviewersModal;
